import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@/_components';
import { Alert } from '@/_components/alert';

import type { EnteredAddressSnapshot } from './address-blocks';
import { ConfirmContent } from './confirm-content';
import { FixContent } from './fix-content';
import { SubpremisesContent } from './subpremises-content';

import type { AddressValidationResponse, RecommendedAction } from '../../shared';

export type AddressValidationModalProps = {
  open: boolean;
  result: AddressValidationResponse | null;
  enteredAddress: EnteredAddressSnapshot | null;
  onKeepCurrent: () => void;
  onUseRecommended: () => void;
  onUseRecommendedWithUnit: (unit: string) => void;
  onEditAddress: () => void;
  onClose: () => void;
};

const actionConfig: Record<
  RecommendedAction,
  { title: string; description: string; alertType: 'success' | 'warning' | 'error' | 'info' }
> = {
  PROCEED: {
    title: 'Verify your address',
    description:
      "To ensure accurate delivery and billing, we checked your address against USPS records. Please choose the address format you'd like to use.",
    alertType: 'success',
  },
  CONFIRM: {
    title: 'Verify your address',
    description:
      "To ensure accurate delivery and billing, we checked your address against USPS records. Please choose the address you'd like to use.",
    alertType: 'info',
  },
  CONFIRM_ADD_SUBPREMISES: {
    title: 'Address Needs Attention',
    description: 'USPS found a partial match for your address but needs additional information to fully verify it.',
    alertType: 'warning',
  },
  FIX: {
    title: 'Address could not be verified',
    description: 'We were unable to verify this address. Please review and correct the address, then try again.',
    alertType: 'error',
  },
};

export function AddressValidationModal({
  open,
  result,
  enteredAddress,
  onKeepCurrent,
  onUseRecommended,
  onUseRecommendedWithUnit,
  onEditAddress,
  onClose,
}: AddressValidationModalProps) {
  if (!result) {
    return null;
  }

  const action = result.recommended_action ?? 'FIX';
  const config = actionConfig[action];
  const hasRecommended = Boolean(result.standardized_address || result.formatted_address);
  const canUseRecommended = action !== 'FIX' && hasRecommended;
  const isFix = action === 'FIX';
  const isSubpremises = action === 'CONFIRM_ADD_SUBPREMISES';

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader>{config.title}</ModalHeader>
      <ModalBody className="space-y-4">
        <Alert type={config.alertType}>
          {isSubpremises ? (
            <>
              <strong>Partial Match &mdash; Missing apartment or unit number</strong>
              <br />
              {config.description}
            </>
          ) : (
            config.description
          )}
        </Alert>

        {isFix ? (
          <FixContent enteredAddress={enteredAddress} />
        ) : isSubpremises ? (
          <SubpremisesContent
            enteredAddress={enteredAddress}
            result={result}
            onKeepCurrent={onKeepCurrent}
            onUseRecommendedWithUnit={onUseRecommendedWithUnit}
          />
        ) : (
          <ConfirmContent enteredAddress={enteredAddress} result={result} hasRecommended={hasRecommended} />
        )}
      </ModalBody>

      {!isSubpremises && (
        <ModalFooter>
          {isFix ? (
            <Button modal onClick={onEditAddress} as="contained">
              Edit Address
            </Button>
          ) : (
            <>
              <Button modal onClick={onKeepCurrent} as="outline">
                Keep Current
              </Button>
              {canUseRecommended && (
                <Button modal onClick={onUseRecommended} as="contained">
                  Update Address
                </Button>
              )}
            </>
          )}
        </ModalFooter>
      )}
    </Modal>
  );
}
